/**
 * Subsystem registry: lifecycle, dependency-ordered init, and the `ctx` surface
 * every subsystem reaches other subsystems through.
 *
 * The rule this file enforces is ARCHITECTURE.md §2: subsystems never import
 * each other. They declare `static deps` and resolve at runtime via `ctx.get`.
 * A dependency cycle is a hard error at registration time, not a mysterious
 * undefined at runtime.
 */

import { HashRegistry } from './hash.js'
import { Rng } from './rng.js'
import { type Config, defaultConfig } from './config.js'

export interface Subsystem {
  init?(ctx: Ctx): void
  fixedUpdate?(tick: number): void
  /**
   * Runs after every subsystem's `fixedUpdate` for the same tick. This is where
   * presentation snapshots belong: capturing interpolation transforms from
   * inside `fixedUpdate` would race the simulation subsystems that have not
   * run yet, and the ordering could not be fixed by `deps` without every
   * presentation subsystem naming every simulation one.
   */
  postFixedUpdate?(tick: number): void
  update?(dt: number): void
  lateUpdate?(dt: number): void
  resize?(width: number, height: number): void
  dispose?(): void
}

export interface SubsystemClass {
  readonly id: string
  readonly deps?: readonly string[]
  new (...args: never[]): Subsystem
}

/** Render-side timing. `alpha` is render-only; reading it in fixedUpdate is a bug. */
export interface Time {
  /** Current simulation tick index. */
  tick: number
  /** Interpolation factor in [0,1) between the previous and current tick. */
  alpha: number
  /** Seconds since the previous render frame. */
  dt: number
}

export type EventHandler = (payload: unknown) => void

export interface Ctx {
  get<T>(id: string): T
  has(id: string): boolean
  readonly time: Time
  /** Seeded, sim-safe. The only source of randomness the simulation may use. */
  readonly rng: Rng
  /** Every unit that adds simulation state registers it here. */
  readonly hash: HashRegistry
  readonly config: Config
  emit(event: string, payload?: unknown): void
  on(event: string, fn: EventHandler): () => void
  off(event: string, fn: EventHandler): void
}

export class DependencyCycleError extends Error {
  constructor(public readonly cycle: string[]) {
    super(`subsystem dependency cycle: ${cycle.join(' -> ')}`)
    this.name = 'DependencyCycleError'
  }
}

export class MissingDependencyError extends Error {
  constructor(
    public readonly subsystem: string,
    public readonly missing: string,
  ) {
    super(`subsystem '${subsystem}' depends on '${missing}', which is not registered`)
    this.name = 'MissingDependencyError'
  }
}

interface Entry {
  id: string
  deps: readonly string[]
  instance: Subsystem
}

export class Registry implements Ctx {
  private readonly entries = new Map<string, Entry>()
  private ordered: Entry[] = []
  private started = false

  /** Subsystems that implement each hook, cached so the hot loops do no lookups. */
  private fixedUpdaters: Subsystem[] = []
  private postFixedUpdaters: Subsystem[] = []
  private updaters: Subsystem[] = []
  private lateUpdaters: Subsystem[] = []
  private resizers: Subsystem[] = []

  private readonly handlers = new Map<string, EventHandler[]>()

  readonly time: Time = { tick: 0, alpha: 0, dt: 0 }
  readonly rng: Rng
  readonly hash: HashRegistry
  readonly config: Config

  constructor(opts: { rng?: Rng; hash?: HashRegistry; config?: Config } = {}) {
    this.rng = opts.rng ?? new Rng(0)
    this.hash = opts.hash ?? new HashRegistry()
    this.config = opts.config ?? defaultConfig()
  }

  /**
   * Register a subsystem instance under its class id. Registration order does
   * not matter; `init()` resolves order from `static deps`.
   */
  add(cls: SubsystemClass, instance: Subsystem): this {
    if (this.started) throw new Error(`cannot register '${cls.id}' after init()`)
    if (this.entries.has(cls.id)) throw new Error(`subsystem '${cls.id}' registered twice`)
    this.entries.set(cls.id, { id: cls.id, deps: cls.deps ?? [], instance })
    return this
  }

  has(id: string): boolean {
    return this.entries.has(id)
  }

  get<T>(id: string): T {
    const entry = this.entries.get(id)
    if (!entry) throw new Error(`no subsystem '${id}' registered`)
    return entry.instance as T
  }

  /** Dependency-ordered list of subsystem ids. Available after `init()`. */
  get order(): string[] {
    return this.ordered.map((e) => e.id)
  }

  /**
   * Topologically sort by `deps`, then `init` each in that order.
   * Throws DependencyCycleError on a cycle and MissingDependencyError on a
   * dangling dep — both before any subsystem's `init` runs, so a bad graph
   * never leaves half-initialized state behind.
   */
  init(): void {
    if (this.started) throw new Error('registry already initialized')
    this.ordered = this.topoSort()
    this.started = true

    for (const entry of this.ordered) entry.instance.init?.(this)

    for (const entry of this.ordered) {
      const s = entry.instance
      if (s.fixedUpdate) this.fixedUpdaters.push(s)
      if (s.postFixedUpdate) this.postFixedUpdaters.push(s)
      if (s.update) this.updaters.push(s)
      if (s.lateUpdate) this.lateUpdaters.push(s)
      if (s.resize) this.resizers.push(s)
    }
  }

  private topoSort(): Entry[] {
    const out: Entry[] = []
    // 0 = unvisited, 1 = on the current DFS stack, 2 = done
    const mark = new Map<string, 0 | 1 | 2>()
    const stack: string[] = []

    // Deterministic traversal: iterate ids in registration order, and each
    // node's deps in declaration order. A Map preserves insertion order, so
    // this is stable across runs.
    const visit = (id: string): void => {
      const state = mark.get(id) ?? 0
      if (state === 2) return
      if (state === 1) {
        const from = stack.indexOf(id)
        throw new DependencyCycleError([...stack.slice(from), id])
      }
      const entry = this.entries.get(id)
      if (!entry) throw new Error(`no subsystem '${id}' registered`)

      mark.set(id, 1)
      stack.push(id)
      for (const dep of entry.deps) {
        if (!this.entries.has(dep)) throw new MissingDependencyError(id, dep)
        visit(dep)
      }
      stack.pop()
      mark.set(id, 2)
      out.push(entry)
    }

    for (const id of this.entries.keys()) visit(id)
    return out
  }

  fixedUpdate(tick: number): void {
    this.time.tick = tick
    for (let i = 0; i < this.fixedUpdaters.length; i++) {
      this.fixedUpdaters[i].fixedUpdate!(tick)
    }
    for (let i = 0; i < this.postFixedUpdaters.length; i++) {
      this.postFixedUpdaters[i].postFixedUpdate!(tick)
    }
  }

  update(dt: number, alpha: number): void {
    this.time.dt = dt
    this.time.alpha = alpha
    for (let i = 0; i < this.updaters.length; i++) this.updaters[i].update!(dt)
    for (let i = 0; i < this.lateUpdaters.length; i++) this.lateUpdaters[i].lateUpdate!(dt)
  }

  resize(width: number, height: number): void {
    for (let i = 0; i < this.resizers.length; i++) this.resizers[i].resize!(width, height)
  }

  /** Dispose in reverse init order, so a subsystem's deps outlive it. */
  dispose(): void {
    for (let i = this.ordered.length - 1; i >= 0; i--) this.ordered[i].instance.dispose?.()
    this.entries.clear()
    this.ordered = []
    this.fixedUpdaters = []
    this.postFixedUpdaters = []
    this.updaters = []
    this.lateUpdaters = []
    this.resizers = []
    this.handlers.clear()
    this.started = false
  }

  emit(event: string, payload?: unknown): void {
    const list = this.handlers.get(event)
    if (!list) return
    // Copy: a handler may unsubscribe itself mid-dispatch.
    for (const fn of list.slice()) fn(payload)
  }

  on(event: string, fn: EventHandler): () => void {
    let list = this.handlers.get(event)
    if (!list) {
      list = []
      this.handlers.set(event, list)
    }
    list.push(fn)
    return () => this.off(event, fn)
  }

  off(event: string, fn: EventHandler): void {
    const list = this.handlers.get(event)
    if (!list) return
    const i = list.indexOf(fn)
    if (i >= 0) list.splice(i, 1)
  }
}
